import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { modal } from '@client/components/_ui/feedback/modal';
import { Link } from '@client/components/Common/Link';
import { RiskAppetiteLabel } from '@client/components/risks/RiskAppetiteLabel';
import {
    type RiskAppetiteStatementAction,
    RiskAppetiteStatementAdd,
} from '@client/components/risks/RiskAppetiteStatementAdd';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { TableExpandButton } from '@client/hooks/table-v2/components/TableExpandButton';
import { openRowOnClick } from '@client/hooks/table-v2/utils/open-row-on-click';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, SquarePlus } from 'lucide-react';
import { type MouseEvent, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/risk/ras/')({
    staticData: {
        ui: {
            title: 'Risk appetite statement',
            subtitle: 'Manage risk appetites for your risk categories',
        },
    },
    component: RiskAppetiteStatementSearch,
});

type QueryParams = RouterInputs['risk']['appetiteStatement']['getRiskCategoryTreeWithAppetites'];
type RiskCategoryData = RouterOutputs['risk']['appetiteStatement']['getRiskCategoryTreeWithAppetites'];
type RiskCategoryRow = NonNullable<RiskCategoryData>['rows'][number];
type OrderByType = NonNullable<QueryParams['orderBy']>;

function RiskAppetiteStatementSearch() {
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const [action, setAction] = useState<RiskAppetiteStatementAction>(['none']);
    const { can } = usePermissions();
    const canManage = can('risk:update');

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'asc' },
        where: undefined,
    });

    const { data, isPending, refetch } = trpc.risk.appetiteStatement.getRiskCategoryTreeWithAppetites.useQuery(input);

    const columnHelper = createColumnHelper<RiskCategoryRow, OrderByType>();

    const columns = [
        columnHelper.display({
            id: 'category',
            header: 'Risk category',
            // The name links to the category's statement, so a keyboard user can open it (the row click is pointer-only).
            // Its ring takes the link colour, as the expand button's does: accent-500 is 2.99:1 on the dark card.
            cell: ({ row }) =>
                row.riskAppetite?.id ? (
                    <Link
                        to="/console/$orgId/risk/ras/details/$id"
                        params={{ orgId, id: row.riskAppetite.id }}
                        className="focus-visible:outline-current"
                    >
                        {row.category.name}
                    </Link>
                ) : (
                    row.category.name
                ),
            hideable: false,
            // Fixed widths, so expanding a row's indented children does not re-flow the columns
            width: 320,
        }),
        columnHelper.display({
            id: 'riskAppetite',
            header: 'Risk appetite',
            cell: ({ row }) => (
                <RiskAppetiteLabel
                    description={row.riskAppetite?.appetite?.description || ''}
                    color={row.riskAppetite?.appetite?.color}
                    name={row.riskAppetite?.appetite?.name}
                    isDeleted={row.riskAppetite?.isDeleted}
                />
            ),
            width: 200,
            className: 'whitespace-nowrap px-5',
        }),
        columnHelper.display({
            id: 'summary',
            header: 'Summary',
            cell: ({ row }) => row.riskAppetite?.summary || '',
        }),
        // The keyboard route to the add flow for an empty category (the empty row's click is pointer-only).
        ...(canManage
            ? [
                  columnHelper.actions({
                      width: 64,
                      cell: ({ row }) =>
                          !row.riskAppetite?.id && row.category?.id ? (
                              <Button.Icon
                                  icon={<Plus />}
                                  tooltip="Add risk appetite statement"
                                  aria-label={`Add risk appetite statement for ${row.category.name}`}
                                  onClick={(e) => handleRowClick(row, e)}
                              />
                          ) : null,
                  }),
              ]
            : []),
    ];

    // A row is interactive when it navigates to an existing statement, or when
    // the caller can create one for an empty category.
    const isRowInteractive = (record: RiskCategoryRow) =>
        Boolean(record?.riskAppetite?.id) || (canManage && Boolean(record?.category?.id));

    const handleRowClick = (record: RiskCategoryRow, e: MouseEvent) => {
        if (record?.riskAppetite?.id) {
            navigate(
                {
                    to: '/console/$orgId/risk/ras/details/$id',
                    params: {
                        orgId,
                        id: record.riskAppetite.id,
                    },
                },
                e,
            );
        } else if (record?.category?.id && canManage) {
            if (record.categoryLevel === 1) {
                setAction(['add', record.category.id, record.categoryHierarchy.map((c) => c.name)]);
                return;
            }
            const ancestors = record.categoryHierarchy.slice(0, -1);

            for (let i = 0; i < ancestors.length; i++) {
                const ancestorId = ancestors[i].id;
                const ancestorName = ancestors[i].name;
                const ancestorLevel = i + 1;

                const findAncestor = (categories: RiskCategoryRow[]): RiskCategoryRow | undefined => {
                    for (const category of categories) {
                        if (category.categoryLevel === ancestorLevel && category.category.id === ancestorId) {
                            return category;
                        }
                        if (category.children.length > 0) {
                            const found = findAncestor(category.children);
                            if (found) return found;
                        }
                    }
                    return undefined;
                };

                const ancestor = findAncestor(data?.rows || []);

                if (!ancestor?.riskAppetite || ancestor.riskAppetite.isDeleted) {
                    modal.warning({
                        title: 'Action required',
                        content: `Before assigning a risk appetite to "${record.category.name}", please first define a risk appetite for the parent category "${ancestorName}". Risk appetites must be assigned in hierarchical order.`,
                    });
                    return;
                }
            }

            setAction(['add', record.category.id, record.categoryHierarchy.map((c) => c.name)]);
        }
    };

    const openRow = openRowOnClick(handleRowClick);

    const tableProps = usePrismaTable<RiskCategoryRow, QueryParams['where'], OrderByType>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'key',
        queryParams: input,
        onParamsChange: setInput,
        pagination: { position: 'both' },
        emptyState: {
            noRecordsTitle: 'No risk categories yet',
            noRecordsDescription: 'Risk categories set up in risk configuration appear here.',
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        expandable: {
            // Only a category with children can expand; a leaf keeps an equal-width spacer so names stay aligned.
            expandIcon: ({ expanded, onExpand, record }) =>
                record.children.length === 0 ? (
                    <div aria-hidden className="-m-2 inline-block p-4">
                        <span className="anticon invisible">
                            <SquarePlus size={16} />
                        </span>
                    </div>
                ) : (
                    <TableExpandButton
                        record={record}
                        expanded={expanded}
                        name={record.category.name}
                        onExpand={onExpand}
                    />
                ),
            expandIconColumnIndex: 0,
        },
    });

    return (
        <PageController route={Route}>
            <PrismaTable
                {...tableProps}
                // A click on the category link or the expand button stays with that control.
                onRow={(record) => (isRowInteractive(record) ? openRow(record) : {})}
            />

            <RiskAppetiteStatementAdd
                action={action}
                onClose={() => {
                    setAction(['none']);
                    void refetch();
                }}
            />
        </PageController>
    );
}
